import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SEO } from '../components/SEO';
import PublicNav from '../components/PublicNav';

/**
 * Investor Relations Page Component
 *
 * Public-facing page for potential investors. Provides information about
 * the company, investment opportunity, and how to get in touch.
 */
export function InvestorRelations(): React.ReactElement {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Investor Relations"
        description="Invest in the future of AI-powered requirements management. Nivoon is a production-ready B2B SaaS platform now accepting strategic investments."
        path="/investor-relations"
      />

      <PublicNav />

      {/* Hero Header */}
      <section className="pt-20 pb-16 bg-gradient-to-b from-slate-900 to-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-emerald-500/20 text-emerald-300 rounded-full text-sm font-medium mb-6 border border-emerald-500/30">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
            Now Accepting Investments
          </div>
          <h1 className="text-5xl font-bold text-white mb-6">
            Investor Relations
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Nivoon is building the future of AI-powered requirements management. We're inviting strategic investors to join us as we scale.
          </p>
        </div>
      </section>

      {/* The Opportunity */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">The Opportunity</h2>
            <p className="text-lg text-gray-600">
              Requirements management is a foundational challenge in software development. Teams waste significant time writing, organizing, and tracking requirements manually. Nivoon combines modern project management with AI to solve this problem at scale.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="text-center p-8 bg-gradient-to-b from-indigo-50 to-white rounded-xl border border-indigo-100">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">$4.5B+</h3>
              <p className="text-gray-600">Global requirements management market, growing at 12% CAGR</p>
            </div>
            <div className="text-center p-8 bg-gradient-to-b from-emerald-50 to-white rounded-xl border border-emerald-100">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">AI-First</h3>
              <p className="text-gray-600">Deep AI integration across the entire platform, not a bolt-on afterthought</p>
            </div>
            <div className="text-center p-8 bg-gradient-to-b from-blue-50 to-white rounded-xl border border-blue-100">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">B2B SaaS</h3>
              <p className="text-gray-600">Multi-tenant architecture built for enterprise teams from day one</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Nivoon */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">Why Nivoon</h2>
          <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-16">
            We've built a production-ready platform with real AI capabilities that teams are already using. Here's what sets us apart.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Deep AI Integration</h3>
                  <p className="text-gray-600">Over 10 AI-powered features including requirements generation, quality scoring, risk identification, test case generation, gap analysis, executive summary generation, chatbot assistance, and persona creation. AI isn't a feature we added -- it's the foundation of the platform.</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Production-Ready Product</h3>
                  <p className="text-gray-600">This isn't a prototype. Nivoon is a fully deployed, production application running on Azure with real users. The platform handles multi-company data isolation, encrypted credential storage, and enterprise-grade security today.</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Enterprise Integrations</h3>
                  <p className="text-gray-600">Native bi-directional JIRA Cloud integration with encrypted API token storage and webhook-based real-time sync. Built to integrate into existing enterprise workflows, not replace them.</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Clear Growth Path</h3>
                  <p className="text-gray-600">A well-defined roadmap for expansion including additional integrations, advanced collaboration features, and deeper AI capabilities. The architecture is designed to scale with growing demand.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Details */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">Current Investment Round</h2>
          <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-16">
            We are currently accepting investments from individuals and firms who share our vision for the future of requirements management.
          </p>

          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-10 text-white shadow-xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div>
                  <h3 className="text-lg font-semibold text-slate-300 uppercase tracking-wider mb-6">What We're Looking For</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-slate-200">Strategic investors who understand B2B SaaS and enterprise software</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-slate-200">Partners who can provide industry connections and go-to-market expertise</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-slate-200">Angel investors and early-stage funds aligned with AI-driven product development</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-slate-200">Individuals passionate about improving how software teams work</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-300 uppercase tracking-wider mb-6">Use of Funds</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-indigo-400 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-slate-200">Engineering team expansion to accelerate feature development</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-indigo-400 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-slate-200">Sales and marketing to drive customer acquisition</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-indigo-400 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-slate-200">Infrastructure scaling for enterprise-grade reliability</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-indigo-400 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-slate-200">Additional AI capabilities and third-party integrations</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Highlights */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">Platform at a Glance</h2>
          <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-16">
            A snapshot of the capabilities already live and in production.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { label: 'AI Features', value: '10+', desc: 'Powered by GPT-4o' },
              { label: 'API Endpoints', value: '60+', desc: 'RESTful architecture' },
              { label: 'RBAC Roles', value: '4', desc: 'Granular permissions' },
              { label: 'Integrations', value: 'JIRA', desc: 'Bi-directional sync' },
              { label: 'Database', value: 'Azure SQL', desc: 'Enterprise-grade' },
              { label: 'Deployment', value: 'CI/CD', desc: 'GitHub Actions' },
              { label: 'Architecture', value: 'Multi-Tenant', desc: 'Company isolation' },
              { label: 'Security', value: 'AES-256', desc: 'Encrypted credentials' },
            ].map((item) => (
              <div key={item.label} className="bg-white rounded-xl p-6 text-center shadow-sm border border-gray-100">
                <p className="text-2xl font-bold text-gray-900">{item.value}</p>
                <p className="text-sm font-semibold text-indigo-600 mt-1">{item.label}</p>
                <p className="text-xs text-gray-500 mt-1">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Interested in Investing?</h2>
          <p className="text-lg text-gray-600 mb-8">
            We'd love to hear from you. Whether you're an angel investor, venture capital firm, or strategic partner, reach out to learn more about the opportunity and schedule a conversation with our team.
          </p>
          <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200 max-w-lg mx-auto">
            <div className="space-y-4">
              <div className="flex items-center gap-3 justify-center">
                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <a href="mailto:investors@nivoon.com" className="text-lg font-semibold text-indigo-600 hover:text-indigo-700 transition-colors">
                  investors@nivoon.com
                </a>
              </div>
              <p className="text-sm text-gray-500">
                Please include a brief introduction and your area of interest. We respond to all inquiries within two business days.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-gray-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-gray-400 text-center leading-relaxed">
            This page is for informational purposes only and does not constitute an offer to sell or a solicitation of an offer to buy any securities. Any investment in Nivoon involves risk, including the potential loss of the entire investment. Prospective investors should conduct their own due diligence and consult with their financial, legal, and tax advisors before making any investment decisions.
          </p>
        </div>
      </section>

      {/* Footer - matches Landing page */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-white font-semibold mb-4">Product</h3>
              <ul className="space-y-2">
                <li><a href="/features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="/pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Security</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Roadmap</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Company</h3>
              <ul className="space-y-2">
                <li><a href="/about" className="hover:text-white transition-colors">About</a></li>
                <li><a href="/blog" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="/careers" className="hover:text-white transition-colors">Careers</a></li>
                <li><span className="text-white">Investor Relations</span></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Resources</h3>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-white transition-colors">Documentation</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Community</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                <li><a href="/legal#privacy" className="hover:text-white transition-colors">Privacy</a></li>
                <li><a href="/legal#terms" className="hover:text-white transition-colors">Terms</a></li>
                <li><a href="/legal#cookie-policy" className="hover:text-white transition-colors">Cookie Policy</a></li>
                <li><a href="/legal#licenses" className="hover:text-white transition-colors">Licenses</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0 cursor-pointer" onClick={() => navigate('/')}>
              <img src="/logo1.png" alt="Nivoon" className="h-8 opacity-70" />
            </div>
            <p className="text-sm text-gray-400">
              &copy; 2025 Nivoon. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default InvestorRelations;
